import React, { useCallback, useMemo, useRef } from "react";
import DynamicLinesDialog from "../../../components/DynamicLinesDialog";
import {
    fetchDataEngine,
    graphqlQuery,
} from "../../../data/Axios/DynamicService";
import { HRService, accountsmanagement } from "../../../config";
import { allAccountSubCategories } from "../../../data/Axios/queries";
import { normalizeExpenseTypes } from "../expenseTypeOptions";

const base_url = HRService.uri;
const accounts_url = accountsmanagement.uri;

const ManageClaimLinesDialog = ({ open, onClose, claimData, extraActions,refetchData }) => {
    const expenseTypesRef = useRef([]);
    const accountsRef = useRef([]);

    // Options loaders
    const loadExpenseTypes = useCallback(async () => {
        const filtered = normalizeExpenseTypes(
            await graphqlQuery(allAccountSubCategories, accounts_url)
        );

        expenseTypesRef.current = filtered;
        return filtered;
    }, []);

    const loadAccounts = useCallback(async (expenseTypeId) => {
        if (!expenseTypeId) return [];
        const accs = await fetchDataEngine(
            "allAccountsByColumnValues",
            "accounts",
            { accountSubcategoryId: Number(expenseTypeId) },
            ["id", "accountNumber", "accountName"],
            accounts_url
        );
        const normalized = (accs || []).map((account) => ({
            ...account,
            id: Number(account.id),
            value: Number(account.id),
            label: account.accountNumber
                ? `${account.accountName} (${account.accountNumber})`
                : account.accountName,
        }));

        accountsRef.current = normalized;
        return normalized;
    }, []);

    const transformSavedRow = useCallback((row) => {
        const expenseType = expenseTypesRef.current.find(
            (type) => type.id === Number(row.expenseTypeId)
        );
        const account = accountsRef.current.find(
            (acc) => acc.id === Number(row.accountId)
        );

        return {
            ...row,
            expenseTypeName:
                row.expenseTypeName ||
                expenseType?.label ||
                (row.expenseTypeId ? `Expense Type ${row.expenseTypeId}` : ""),
            accountName:
                row.accountName ||
                account?.label ||
                (row.accountId ? `Account ${row.accountId}` : ""),
        };
    }, []);

    // Form fields configuration
    const formFields = useMemo(() => [
        {
            name: "expenseTypeId",
            label: "Expense Type",
            type: "select",
        },
        {
            name: "accountId",
            label: "Account",
            type: "dependent-select",
            dependsOn: "expenseTypeId",
        },
        {
            name: "amount",
            label: "Amount",
            type: "number",
        },
        {
            name: "description",
            label: "Description",
            type: "text",
        },
    ], []);

    // Table columns configuration
    const columns = useMemo(() => [
        { field: "id", headerName: "ID", flex: 0.4 },
        { field: "expenseTypeName", headerName: "Expense Type", flex: 1 },
        { field: "accountName", headerName: "Account", flex: 1 },
        { field: "description", headerName: "Description", flex: 2 },
        { field: "amount", headerName: "Amount", flex: 1, format: "currency" },
    ], []);

    // Query configuration
    const queryConfig = useMemo(() => ({
        name: "claimApplicationLinesByImprestId",
        modelName: "claimApplicationLines",
        inputField: "applicationId",
        fields: ["id", "expenseTypeId", "accountId", "description", "amount", "isActive"],
    }), []);

    // Mutation configuration
    const mutationConfig = useMemo(() => ({
        create: "createClaimLine",
        update: "updateClaimLine",
        modelName: "claimLine",
    }), []);

    // Options loaders map
    const optionsLoaders = useMemo(() => ({
        expenseTypeId: loadExpenseTypes,
        accountId: loadAccounts,
    }), [loadAccounts, loadExpenseTypes]);

    return (
        <DynamicLinesDialog
            open={open}
            onClose={onClose}
            title="Manage Claim Lines"
            parentData={claimData}
            parentIdField="applicationId"
            baseUrl={base_url}
            refetchData={refetchData}
            queryConfig={queryConfig}
            mutationConfig={mutationConfig}
            formFields={formFields}
            columns={columns}
            optionsLoaders={optionsLoaders}
            additionalMutationData={{ isActive: 1 }}
            transformSavedRow={transformSavedRow}
            headerActions={extraActions}
        />
    );
};

export default ManageClaimLinesDialog;
